/**
 * Recurrence Rule Mapper
 *
 * Maps between expo-calendar RecurrenceRule and app's RecurrenceRule type.
 */

import * as Calendar from 'expo-calendar';
import type { RecurrenceRule, DayOfWeek } from '@/types/events';

export class RecurrenceRuleMapper {
  /**
   * Map expo-calendar RecurrenceRule to our RecurrenceRule type
   */
  static mapFromExpo(
    expoRule: Calendar.RecurrenceRule | null | undefined,
  ): RecurrenceRule | null {
    if (!expoRule || !expoRule.frequency) {
      return null;
    }

    // Map frequency
    let frequency: RecurrenceRule['frequency'];
    switch (expoRule.frequency) {
      case Calendar.Frequency.DAILY:
        frequency = 'DAILY';
        break;
      case Calendar.Frequency.WEEKLY:
        frequency = 'WEEKLY';
        break;
      case Calendar.Frequency.MONTHLY:
        frequency = 'MONTHLY';
        break;
      case Calendar.Frequency.YEARLY:
        frequency = 'YEARLY';
        break;
      default:
        return null;
    }

    // Map interval (default to 1)
    const interval = expoRule.interval ?? 1;

    // Map days of week (iOS uses daysOfTheWeek array)
    let byDay: DayOfWeek[] | undefined;
    let bySetPos: number | undefined;
    if (expoRule.daysOfTheWeek) {
      byDay = expoRule.daysOfTheWeek
        .map((day) => this.mapExpoDayOfWeek(day.dayOfTheWeek))
        .filter((d): d is DayOfWeek => d !== null);

      // Extract weekNumber (ordinal position) from first entry if present
      const firstEntry = expoRule.daysOfTheWeek[0];
      if (firstEntry?.weekNumber !== undefined && firstEntry.weekNumber !== 0) {
        bySetPos = firstEntry.weekNumber;
      }
    }

    // Map month day (iOS uses daysOfTheMonth)
    const byMonthDay = expoRule.daysOfTheMonth?.[0] ?? undefined;

    // Map month (iOS uses monthsOfTheYear)
    const byMonth = expoRule.monthsOfTheYear?.[0] ?? undefined;

    // Map end condition
    let endCondition: RecurrenceRule['endCondition'];
    if (expoRule.endDate) {
      // Ends on a specific date
      const endDateStr = this.extractDate(expoRule.endDate);
      endCondition = { type: 'until', until: endDateStr };
    } else if (expoRule.occurrence && expoRule.occurrence > 0) {
      // Ends after N occurrences
      endCondition = { type: 'count', count: expoRule.occurrence };
    } else {
      // Never ends
      endCondition = { type: 'never' };
    }

    return {
      frequency,
      interval,
      byDay,
      byMonthDay,
      byMonth,
      bySetPos,
      endCondition,
    };
  }

  /**
   * Map our RecurrenceRule to expo-calendar RecurrenceRule
   */
  static mapToExpo(rule: RecurrenceRule): Calendar.RecurrenceRule {
    // Map frequency
    let frequency: Calendar.Frequency;
    switch (rule.frequency) {
      case 'DAILY':
        frequency = Calendar.Frequency.DAILY;
        break;
      case 'WEEKLY':
        frequency = Calendar.Frequency.WEEKLY;
        break;
      case 'MONTHLY':
        frequency = Calendar.Frequency.MONTHLY;
        break;
      case 'YEARLY':
        frequency = Calendar.Frequency.YEARLY;
        break;
      default: {
        const _exhaustive: never = rule.frequency;
        throw new Error(`Unsupported recurrence frequency: ${_exhaustive}`);
      }
    }

    const expoRule: Calendar.RecurrenceRule = {
      frequency,
      interval: rule.interval,
    };

    // Map days of week (with optional ordinal position)
    if (rule.byDay && rule.byDay.length > 0) {
      expoRule.daysOfTheWeek = rule.byDay.map((day) => ({
        dayOfTheWeek: this.mapDayOfWeekToExpo(day),
        ...(rule.bySetPos !== undefined ? { weekNumber: rule.bySetPos } : {}),
      }));
    }

    // Map month day
    if (rule.byMonthDay !== undefined) {
      expoRule.daysOfTheMonth = [rule.byMonthDay];
    }

    // Map month
    if (rule.byMonth !== undefined) {
      expoRule.monthsOfTheYear = [rule.byMonth];
    }

    // Map end condition
    if (!rule.endCondition) {
      return expoRule;
    }

    switch (rule.endCondition.type) {
      case 'count':
        expoRule.occurrence = rule.endCondition.count;
        break;
      case 'until':
        expoRule.endDate = new Date(rule.endCondition.until);
        break;
      case 'never':
        // No end date or occurrence
        break;
    }

    return expoRule;
  }

  /**
   * Map expo-calendar DayOfTheWeek to our DayOfWeek type
   */
  private static mapExpoDayOfWeek(
    expoDay: number | null | undefined,
  ): DayOfWeek | null {
    switch (expoDay) {
      case 1:
        return 'SU';
      case 2:
        return 'MO';
      case 3:
        return 'TU';
      case 4:
        return 'WE';
      case 5:
        return 'TH';
      case 6:
        return 'FR';
      case 7:
        return 'SA';
      default:
        return null;
    }
  }

  /**
   * Map our DayOfWeek to expo-calendar DayOfTheWeek (1=Sunday, 7=Saturday)
   */
  private static mapDayOfWeekToExpo(day: DayOfWeek): number {
    switch (day) {
      case 'SU':
        return 1;
      case 'MO':
        return 2;
      case 'TU':
        return 3;
      case 'WE':
        return 4;
      case 'TH':
        return 5;
      case 'FR':
        return 6;
      case 'SA':
        return 7;
    }
  }

  /**
   * Extract date string (YYYY-MM-DD) from Date object
   */
  private static extractDate(date: string | Date | null | undefined): string {
    if (!date) return new Date().toISOString().split('T')[0];

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
